const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_URL || "";

const TOKEN_KEY = "ugongisan_auth_token";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

export interface WorkReport {
  id: string;
  user_id: string;
  site_name: string;
  work_type: string;
  work_description: string;
  worker_count: number;
  work_hours: number;
  region: string;
  weather: string;
  work_date: string;
  remarks: string;
  image_urls?: string[];
  created_at: string;
  updated_at: string;
  profile?: { name: string; role: string } | null;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, options: { method?: string; body?: string } = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE_URL}/api${path}`, { method: options.method, body: options.body, headers });

  if (res.status === 401) {
    setToken(null);
  }

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const message =
      (data as { message?: string } | null)?.message || "요청 중 오류가 발생했습니다.";
    throw new ApiError(message, res.status);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export async function uploadImage(file: File): Promise<string> {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);

  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/upload`, {
      method: "POST",
      headers,
      body: formData,
    });
  } catch {
    throw new ApiError(
      `서버에 연결할 수 없습니다. (${API_BASE_URL || "서버 주소 미설정"}) 네트워크와 서버 주소를 확인해주세요.`,
      0
    );
  }

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const message =
      (data as { message?: string } | null)?.message || "이미지 업로드에 실패했습니다.";
    throw new ApiError(message, res.status);
  }

  return (data as { url: string }).url;
}

// 서버가 반환하는 상대경로(/uploads/...)를 API 서버 절대경로로 변환
export function resolveImageUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
    return url;
  }
  if (url.startsWith("/")) {
    return `${API_BASE_URL}${url}`;
  }
  return `${API_BASE_URL}/${url}`;
}